import { cn } from '@my/ui';
import { MyIIcon } from '@my/ui/icons';
import type { InvoiceStatus } from '../lib/types';

/** The blinking MyI coin that opens the finance assistant — rendered per tab. */
export function AssistantButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Open MyI finance assistant"
      title="my i"
      className="shrink-0 rounded-full p-0.5 transition-colors hover:bg-slate-100"
    >
      <MyIIcon className="h-7 w-7 animate-pulse" />
    </button>
  );
}

/** Headline metric card used across the invoicing tabs. */
export function StatCard({
  label,
  value,
  sub,
  tone = 'default',
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'default' | 'danger' | 'success';
}) {
  const valueTone =
    tone === 'danger' ? 'text-red-600' : tone === 'success' ? 'text-emerald-600' : 'text-slate-900';
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className={cn('mt-1 text-2xl font-semibold tabular-nums', valueTone)}>{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-slate-400">{sub}</p>}
    </div>
  );
}

const STATUS_STYLE: Record<InvoiceStatus, string> = {
  Draft: 'bg-slate-100 text-slate-600 ring-slate-200',
  Issued: 'bg-sky-100 text-sky-700 ring-sky-200',
  Sent: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
  Paid: 'bg-green-100 text-green-700 ring-green-200',
  Overdue: 'bg-red-100 text-red-700 ring-red-200',
  // Muted rather than alarming: a voided invoice is settled business, not a
  // problem needing attention the way an overdue one is.
  Cancelled: 'bg-slate-100 text-slate-500 line-through ring-slate-200',
};

export function StatusBadge({ status }: { status: InvoiceStatus }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1',
        STATUS_STYLE[status],
      )}
    >
      {status}
    </span>
  );
}

/** Eligibility marker: green = eligible, amber = warning (overridable), red = stopper. */
export function StatusDot({ tone, title }: { tone: 'ok' | 'warn' | 'stop'; title?: string }) {
  const color = tone === 'ok' ? 'bg-emerald-500' : tone === 'warn' ? 'bg-amber-500' : 'bg-red-500';
  return (
    <span
      title={title}
      aria-hidden="true"
      className={cn('inline-block h-2.5 w-2.5 shrink-0 rounded-full', color)}
    />
  );
}

/** Dark per-customer group header row that spans the table. */
export function CustomerGroupHeader({
  name,
  code,
  meta,
  action,
}: {
  name: string;
  code?: string;
  meta?: string;
  action?: React.ReactNode;
}) {
  const initials = name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div className="flex min-h-9 items-center gap-2 bg-slate-700 px-3 py-1 text-white">
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-600 text-[9px] font-semibold">
        {initials}
      </span>
      <span className="text-xs font-semibold">{name}</span>
      {code && <span className="text-[11px] text-slate-400">{code}</span>}
      {meta && <span className="text-[11px] text-slate-400">· {meta}</span>}
      {action && <span className="ml-auto">{action}</span>}
    </div>
  );
}

/** Chip used across the finance toolbars (mirrors the Manifesting leg chips). */
function chipClass(active: boolean): string {
  return cn(
    'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
    active
      ? 'border-slate-700 bg-slate-700 text-white'
      : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700',
  );
}

/** Vertical divider, matching the Manifesting toolbar separators. */
export function ToolbarDivider() {
  return <div className="mx-1 h-7 w-px bg-slate-200" aria-hidden="true" />;
}

/** Group by customer / All … — as a chip toggle, not radios. */
export function GroupToggle({
  grouped,
  onChange,
  flatLabel = 'All orders',
}: {
  grouped: boolean;
  onChange: (grouped: boolean) => void;
  /** Label for the flat (ungrouped) chip — e.g. "All invoices" on the summary. */
  flatLabel?: string;
}) {
  return (
    <div className="inline-flex items-center gap-1.5">
      <button type="button" onClick={() => onChange(true)} className={chipClass(grouped)}>
        Group by customer
      </button>
      <button type="button" onClick={() => onChange(false)} className={chipClass(!grouped)}>
        {flatLabel}
      </button>
    </div>
  );
}
